"use client";

import { cn } from "@workspace/ui/lib/utils";
import {
  type MotionProps,
  motion,
  type UseInViewOptions,
  useInView,
  type Variants,
} from "motion/react";
import { useEffect, useRef, useState } from "react";

type AnimationVariant =
  | "fadeIn"
  | "blurIn"
  | "blurInUp"
  | "blurInDown"
  | "slideUp"
  | "slideDown"
  | "slideLeft"
  | "slideRight"
  | "scaleUp"
  | "scaleDown";

interface TypingTextProps extends Omit<MotionProps, "children"> {
  /** Text to animate */
  text?: string;
  /** Array of texts to cycle through */
  texts?: string[];
  /** Typing speed in milliseconds */
  speed?: number;
  /** Delay before starting animation */
  delay?: number;
  /** Whether to show cursor */
  showCursor?: boolean;
  /** Cursor character */
  cursor?: string;
  /** Cursor className */
  cursorClassName?: string;
  /** Whether to loop through texts */
  loop?: boolean;
  /** Pause duration between loops */
  pauseDuration?: number;
  /** Custom className */
  className?: string;
  /** Callback when typing completes */
  onComplete?: () => void;
  /** Whether to start animation when component enters viewport */
  startOnView?: boolean;
  /** Whether to animate only once */
  once?: boolean;
  /** The animation preset to use */
  animation?: AnimationVariant;
  /** Margin for in-view detection (rootMargin) */
  inViewMargin?: UseInViewOptions["margin"];
  /** Manual trigger to start typing */
  start?: boolean;
  /** Whether to reserve space for the full text to avoid layout shifts */
  reserveSpace?: boolean;
}

const cursorVariants: Variants = {
  blinking: {
    opacity: [0, 0, 1, 1],
    transition: {
      duration: 1,
      repeat: Infinity,
      repeatDelay: 0,
      ease: "linear",
      times: [0, 0.5, 0.5, 1],
    },
  },
};

export function TypingText({
  text,
  texts,
  speed = 100,
  delay = 0,
  showCursor = true,
  cursorClassName = "",
  cursor = "|",
  loop = false,
  pauseDuration = 2000,
  className,
  onComplete,
  startOnView = true,
  once = false,
  inViewMargin,
  start = true,
  reserveSpace = false,
  ...props
}: TypingTextProps) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, {
    once,
    margin: inViewMargin as UseInViewOptions["margin"],
  });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(false);
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // Determine if we should start animation
  const shouldStart =
    start && (!startOnView || (isInView && (!once || !hasAnimated)));

  const textArray = texts && texts.length > 0 ? texts : [text];
  const currentText = textArray[currentTextIndex] ?? "";

  useEffect(() => {
    if (!shouldStart) return;
    const timeout = setTimeout(() => {
      setIsTyping(true);
      setHasAnimated(true);
    }, delay);

    return () => clearTimeout(timeout);
  }, [delay, shouldStart]);

  useEffect(() => {
    if (!isTyping) {
      if (!start) {
        setCurrentIndex(0);
      }
      return;
    }

    // If speed is 0, show full text immediately
    if (speed === 0) {
      setCurrentIndex(currentText.length);
      onComplete?.();
      return;
    }

    if (currentIndex < currentText.length) {
      const timeout = setTimeout(() => {
        setCurrentIndex(currentIndex + 1);
      }, speed);

      return () => clearTimeout(timeout);
    } else {
      // Typing complete
      onComplete?.();

      if (loop && texts && texts.length > 1) {
        const timeout = setTimeout(() => {
          setCurrentIndex(0);
          setCurrentTextIndex((prev) => (prev + 1) % texts.length);
        }, pauseDuration);

        return () => clearTimeout(timeout);
      }
    }
  }, [
    currentIndex,
    currentText,
    isTyping,
    speed,
    loop,
    texts,
    pauseDuration,
    onComplete,
    start,
  ]);

  // Animation variants for container (fadeIn only, no y-shift to prevent layout issues)
  const finalVariants = {
    container: {
      hidden: { opacity: 0 },
      show: { opacity: 1, transition: { staggerChildren: 0.02 } },
      exit: { opacity: 0 },
    },
  };
  const MotionComponent = motion.span;

  // Merge passed animate prop with internal animation
  const showState = { opacity: 1 };
  const passedAnimate = props.animate;
  const mergedAnimate =
    passedAnimate && typeof passedAnimate === "object" && !startOnView
      ? { ...showState, ...passedAnimate }
      : (passedAnimate ?? (startOnView ? undefined : "show"));

  return (
    <MotionComponent
      {...props}
      animate={mergedAnimate}
      className={cn("whitespace-pre-wrap inline-block relative", className)}
      exit="exit"
      initial="hidden"
      ref={ref}
      variants={finalVariants.container as Variants}
      viewport={{ once }}
      whileInView={startOnView ? "show" : undefined}
    >
      {/* Visible typed text - direct text node to work with bg-clip-text */}
      {currentText.slice(0, currentIndex)}
      {/* Cursor - inline with explicit color to override text-transparent */}
      {showCursor && (
        <motion.span
          animate="blinking"
          className={cn(
            "inline font-normal text-foreground select-none",
            cursorClassName
          )}
          style={{ background: "none", WebkitBackgroundClip: "unset" }}
          variants={cursorVariants}
        >
          {cursor}
        </motion.span>
      )}
      {/* Invisible remaining text to reserve space */}
      {reserveSpace && (
        <span aria-hidden="true" className="invisible select-none">
          {currentText.slice(currentIndex)}
        </span>
      )}
    </MotionComponent>
  );
}
